import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Crown,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  GripVertical,
} from "lucide-react";
import {
  getAll,
  create,
  update,
  remove,
} from "../../services/firestoreService";
import { updateCollectionVersion } from "../../services/versionService";
import { SEED_PACKAGES } from "../../data/seed";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Badge } from "../ui/badge";
import SeedDataButton from "./SeedDataButton";

const COLLECTION = "packages";

const EMPTY_FORM = {
  name: "",
  tier: "Wedding Package",
  price: "",
  unit: "Juta",
  currency: "IDR",
  tagline: "",
  features: [],
  highlight: false,
};

export default function PackagesEditor() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [featureInput, setFeatureInput] = useState("");
  const [saving, setSaving] = useState(false);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAll(COLLECTION);
      setItems(data);
    } catch {
      toast.error("Gagal memuat paket");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFeatureInput("");
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name || "",
      tier: item.tier || "Wedding Package",
      price: item.price || "",
      unit: item.unit || "Juta",
      currency: item.currency || "IDR",
      tagline: item.tagline || "",
      features: item.features || [],
      highlight: item.highlight || false,
    });
    setFeatureInput("");
    setDialogOpen(true);
  };

  const addFeature = () => {
    if (!featureInput.trim()) return;
    setForm({
      ...form,
      features: [...form.features, featureInput.trim()],
    });
    setFeatureInput("");
  };

  const removeFeature = (index) => {
    setForm({
      ...form,
      features: form.features.filter((_, i) => i !== index),
    });
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Nama wajib diisi");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await update(COLLECTION, editing.id, form);
        toast.success("Paket berhasil diperbarui!");
      } else {
        await create(COLLECTION, form);
        toast.success("Paket berhasil dibuat!");
      }
      await updateCollectionVersion(COLLECTION);
      setDialogOpen(false);
      loadItems();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await remove(COLLECTION, deleteTarget.id);
      toast.success("Paket berhasil dihapus!");
      await updateCollectionVersion(COLLECTION);
      setDeleteTarget(null);
      loadItems();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-1 h-6 bg-[#d4af37] rounded-full" />
            <h1 className="text-2xl font-display text-[#fdfbf7]">Packages</h1>
          </div>
          <p className="text-sm text-[#a6a196] ml-4">Kelola paket wedding</p>
        </div>
        <div className="flex items-center gap-2">
          <SeedDataButton
            collectionName={COLLECTION}
            data={SEED_PACKAGES}
            onComplete={loadItems}
            isEmpty={items.length === 0}
          />
          <Button
            onClick={openCreate}
            className="bg-[#d4af37] text-[#0a0908] hover:bg-[#d4af37]/90"
          >
            <Plus className="w-4 h-4 mr-1" /> Tambah
          </Button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-[#d4af37] animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-[#a6a196] border border-dashed border-[#d4af37]/20 rounded-lg">
          <Crown className="w-12 h-12 mb-3 opacity-20" />
          <p className="mb-4 text-sm">Belum ada paket</p>
          <Button
            onClick={openCreate}
            variant="outline"
            className="border-[#d4af37]/30 text-[#d4af37]"
          >
            <Plus className="w-4 h-4 mr-1" /> Tambah Paket
          </Button>
        </div>
      ) : (
        <>
          {/* Mobile: Card view */}
          <div className="block space-y-3 md:hidden">
            {items.map((item) => (
              <div
                key={item.id}
                className="bg-[#15120f] border border-[#d4af37]/10 rounded-lg p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="mb-2">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm text-[#fdfbf7] font-medium">
                          {item.name}
                        </p>
                        {item.highlight && (
                          <Badge className="bg-[#d4af37]/10 text-[#d4af37] border-[#d4af37]/20 text-[10px] px-1.5 py-0.5">
                            Rekomendasi
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-[#a6a196]">{item.tier}</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-[#d4af37] font-medium">
                        {item.currency} {item.price} {item.unit}
                      </span>
                      <span className="text-xs text-[#a6a196]">
                        {item.features?.length || 0} fitur
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEdit(item)}
                      className="h-8 w-8 text-[#a6a196] hover:text-[#d4af37] hover:bg-[#d4af37]/10"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeleteTarget(item)}
                      className="h-8 w-8 text-[#a6a196] hover:text-red-400 hover:bg-red-400/10"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: Table view */}
          <div className="hidden md:block rounded-lg border border-[#d4af37]/10 overflow-hidden bg-[#0a0908]">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#d4af37]/10">
                    <th className="text-left text-xs text-[#a6a196] uppercase tracking-wider px-4 py-3 font-medium">
                      Name
                    </th>
                    <th className="text-left text-xs text-[#a6a196] uppercase tracking-wider px-4 py-3 font-medium">
                      Tier
                    </th>
                    <th className="text-left text-xs text-[#a6a196] uppercase tracking-wider px-4 py-3 font-medium">
                      Price
                    </th>
                    <th className="text-left text-xs text-[#a6a196] uppercase tracking-wider px-4 py-3 font-medium">
                      Features
                    </th>
                    <th className="text-left text-xs text-[#a6a196] uppercase tracking-wider px-4 py-3 font-medium">
                      Highlight
                    </th>
                    <th className="text-right text-xs text-[#a6a196] uppercase tracking-wider px-4 py-3 font-medium">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#d4af37]/5">
                  {items.map((item) => (
                    <tr
                      key={item.id}
                      className="hover:bg-[#15120f]/50 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <p className="text-sm text-[#fdfbf7] font-medium">
                          {item.name}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-[#a6a196]">
                          {item.tier}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-[#d4af37] font-medium">
                          {item.currency} {item.price} {item.unit}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <Badge
                          variant="outline"
                          className="border-[#d4af37]/20 text-[#a6a196]"
                        >
                          {item.features?.length || 0} fitur
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        {item.highlight ? (
                          <Badge className="bg-[#d4af37]/10 text-[#d4af37] border-[#d4af37]/20">
                            Rekomendasi
                          </Badge>
                        ) : (
                          <span className="text-sm text-[#a6a196]/50">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEdit(item)}
                            className="h-8 w-8 text-[#a6a196] hover:text-[#d4af37] hover:bg-[#d4af37]/10"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteTarget(item)}
                            className="h-8 w-8 text-[#a6a196] hover:text-red-400 hover:bg-red-400/10"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#15120f] border-[#d4af37]/20 text-[#fdfbf7] sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#fdfbf7]">
              {editing ? "Edit Paket" : "Tambah Paket"}
            </DialogTitle>
            <DialogDescription className="text-[#a6a196]">
              {editing
                ? "Ubah detail paket wedding"
                : "Isi detail paket wedding baru"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm text-[#a6a196]">Nama</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Nama paket"
                  className="bg-[#0a0908] border-[#d4af37]/20 text-[#fdfbf7] placeholder:text-[#a6a196]/50"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-[#a6a196]">Tier</label>
                <Input
                  value={form.tier}
                  onChange={(e) => setForm({ ...form, tier: e.target.value })}
                  placeholder="Wedding Package"
                  className="bg-[#0a0908] border-[#d4af37]/20 text-[#fdfbf7] placeholder:text-[#a6a196]/50"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm text-[#a6a196]">Price</label>
                <Input
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  placeholder="2"
                  className="bg-[#0a0908] border-[#d4af37]/20 text-[#fdfbf7] placeholder:text-[#a6a196]/50"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-[#a6a196]">Unit</label>
                <Input
                  value={form.unit}
                  onChange={(e) => setForm({ ...form, unit: e.target.value })}
                  placeholder="Juta"
                  className="bg-[#0a0908] border-[#d4af37]/20 text-[#fdfbf7] placeholder:text-[#a6a196]/50"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-[#a6a196]">Currency</label>
                <Select
                  value={form.currency}
                  onValueChange={(v) => setForm({ ...form, currency: v })}
                >
                  <SelectTrigger className="bg-[#0a0908] border-[#d4af37]/20 text-[#fdfbf7]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#15120f] border-[#d4af37]/20 text-[#fdfbf7]">
                    <SelectItem
                      value="IDR"
                      className="hover:bg-[#d4af37]/10 focus:bg-[#d4af37]/10"
                    >
                      IDR
                    </SelectItem>
                    <SelectItem
                      value="USD"
                      className="hover:bg-[#d4af37]/10 focus:bg-[#d4af37]/10"
                    >
                      USD
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-[#a6a196]">Tagline</label>
              <Input
                value={form.tagline}
                onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                placeholder="Sakral, ringkas, dan paripurna"
                className="bg-[#0a0908] border-[#d4af37]/20 text-[#fdfbf7] placeholder:text-[#a6a196]/50"
              />
            </div>

            {/* Features */}
            <div className="space-y-2">
              <label className="text-sm text-[#a6a196] font-medium">
                Fitur
              </label>
              <div className="flex gap-2">
                <Input
                  value={featureInput}
                  onChange={(e) => setFeatureInput(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && (e.preventDefault(), addFeature())
                  }
                  placeholder="Tambah fitur..."
                  className="bg-[#0a0908] border-[#d4af37]/20 text-[#fdfbf7] placeholder:text-[#a6a196]/50"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={addFeature}
                  className="border-[#d4af37]/30 text-[#d4af37] shrink-0"
                >
                  Tambah
                </Button>
              </div>
              {form.features.length > 0 && (
                <div className="space-y-1.5 mt-2">
                  {form.features.map((feat, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#0a0908] border border-[#d4af37]/10 group"
                    >
                      <GripVertical className="w-3 h-3 text-[#a6a196]/30 shrink-0" />
                      <span className="text-sm text-[#fdfbf7] flex-1">
                        {feat}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeFeature(i)}
                        className="text-[#a6a196] hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Highlight toggle */}
            <div className="flex items-center justify-between py-2">
              <div>
                <label className="text-sm text-[#fdfbf7] font-medium">
                  Sorotan / Rekomendasi
                </label>
                <p className="text-xs text-[#a6a196]">
                  Tandai sebagai paket rekomendasi
                </p>
              </div>
              <Switch
                checked={form.highlight}
                onCheckedChange={(v) => setForm({ ...form, highlight: v })}
                className="data-[state=checked]:bg-[#d4af37]"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="border-[#d4af37]/20 text-[#a6a196]"
            >
              Batal
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#d4af37] text-[#0a0908] hover:bg-[#d4af37]/90"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" /> Menyimpan...
                </>
              ) : editing ? (
                "Perbarui"
              ) : (
                "Buat"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={() => setDeleteTarget(null)}
      >
        <AlertDialogContent className="bg-[#15120f] border-[#d4af37]/20 text-[#fdfbf7]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#fdfbf7]">
              Hapus Paket
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#a6a196]">
              Apakah Anda yakin ingin menghapus "{deleteTarget?.name}"? Tindakan
              ini tidak dapat dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#0a0908] border-[#d4af37]/20 text-[#a6a196] hover:bg-[#15120f]">
              Batal
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="text-red-400 border bg-red-500/10 border-red-500/20 hover:bg-red-500/20"
            >
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
